import { useCallback, useState } from 'react'
import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community'
import _ from 'lodash'
import {
  RestoreType,
  useBulkSheetState,
} from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { UiStateKey } from '../../../../lib/commons/uiStates'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'

// ag-grid の getFilterModel/setFilterModel が扱うフィルタ表現(型 export が無いため Record で表す)。
type FilterModel = Record<string, unknown>

// 配列リテラルを毎レンダー渡すと内部の useMemo/useEffect が連鎖発火して uiState API を無限コールするため、
// モジュールスコープの定数で参照を固定する。
const RESTORE_TYPES: RestoreType[] = ['COLUMN_STATE', 'FILTER_STATE']

const toFilteredColumns = (
  gridApi: GridApi,
  filterModel: FilterModel
): ColDef[] =>
  Object.keys(filterModel)
    .map(colKey => gridApi.getColumnDef(colKey))
    .filter((col): col is ColDef => !!col)

const toSortedColumnState = (
  gridApi: GridApi,
  columnState: ColumnState[]
): SortedColumnState[] =>
  columnState
    .filter(col => !!col.sort)
    .map(col => {
      const def = gridApi.getColumnDef(col.colId)
      return {
        colId: col.colId,
        field: def?.field,
        headerName: def?.headerName,
        sort: col.sort,
      }
    })

/**
 * サブステータス設定画面のヘッダーツールバー(フィルタ / ソート列 / 列設定)用の状態。
 * ag-grid の列状態・フィルタ状態を UiState に永続化する(SprintReportNew と同じ方式)。
 */
export const useSubStatusBulkSheetState = (
  gridApi: GridApi | undefined,
  functionUuid: string,
  projectUuid: string
) => {
  const base = useBulkSheetState(
    functionUuid,
    projectUuid,
    UiStateKey.SubStatusColumnAndFilterState,
    RESTORE_TYPES,
    projectUuid
  )

  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )

  const restoreColumnState = useCallback(() => {
    if (!gridApi || _.isEmpty(base.columnState)) return
    gridApi.applyColumnState({ state: base.columnState, applyOrder: true })
  }, [base.columnState, gridApi])

  const syncDerivedStateFromRestored = useCallback(() => {
    if (!gridApi) return
    if (!_.isEmpty(base.filterState)) {
      setFilteredColumns(toFilteredColumns(gridApi, base.filterState ?? {}))
    }
    setSortColumnsState(toSortedColumnState(gridApi, base.columnState ?? []))
  }, [gridApi, base.columnState, base.filterState])

  const onFirstDataRendered = useCallback(
    (_e: FirstDataRenderedEvent) => {
      restoreColumnState()
      if (!_.isEmpty(base.filterState)) {
        gridApi?.setFilterModel(base.filterState ?? null)
      }
      syncDerivedStateFromRestored()
      base.finishRestoring()
    },
    [base, gridApi, restoreColumnState, syncDerivedStateFromRestored]
  )

  const rememberColumnState = useCallback(() => {
    const columnState = gridApi?.getColumnState()
    columnState && base.saveColumnState(columnState)
  }, [base, gridApi])

  const refreshSortColumnsState = useCallback(() => {
    if (!gridApi) return
    setSortColumnsState(toSortedColumnState(gridApi, gridApi.getColumnState()))
  }, [gridApi])

  const onSortChanged = useCallback(
    (_e: SortChangedEvent) => {
      rememberColumnState()
      refreshSortColumnsState()
    },
    [rememberColumnState, refreshSortColumnsState]
  )

  const onColumnMoved = useCallback(
    (_e: ColumnMovedEvent) => {
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onColumnResized = useCallback(
    (e: ColumnResizedEvent) => {
      // ドラッグ中は finished=false で連続発火するので、終端のみ保存
      if (!e.finished) return
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onColumnVisible = useCallback(
    (_e: ColumnVisibleEvent) => {
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterModel = e.api.getFilterModel()
      base.saveFilterState(filterModel)
      setFilteredColumns(toFilteredColumns(e.api, filterModel))
      // フィルタ/ソート適用中はドラッグ並べ替えを無効化する(並び順が視覚と一致しなくなるため)。
      const hasSorted = e.api.getColumnState().some(col => col.sort != null)
      const hasFilters = Object.keys(filterModel).length > 0
      e.api.setGridOption('suppressRowDrag', hasSorted || hasFilters)
    },
    [base]
  )

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!gridApi) return
      const colKey = column.colId || column.field || ''
      const { [colKey]: _omit, ...rest } = gridApi.getFilterModel()
      gridApi.setFilterModel(rest)
    },
    [gridApi]
  )

  const resetFilters = useCallback(() => {
    if (!gridApi) return
    gridApi.setFilterModel(null)
  }, [gridApi])

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef) => {
      if (!gridApi) return
      const colIdStr = typeof colId === 'string' ? colId : colId.colId || ''
      gridApi.applyColumnState({ state: [{ colId: colIdStr, sort: null }] })
    },
    [gridApi]
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridApi) return
    gridApi.applyColumnState({ defaultState: { sort: null } })
  }, [gridApi])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef, sort: 'asc' | 'desc' | null) => {
      if (!gridApi) return
      const colIdStr = typeof colId === 'string' ? colId : colId.colId || ''
      gridApi.applyColumnState({ state: [{ colId: colIdStr, sort }] })
    },
    [gridApi]
  )

  // 列設定ポッパーの「初期化」操作後に派生 state を再同期する(reset*** はイベントを発火しないため)。
  const resetDerivedState = useCallback(() => {
    setFilteredColumns([])
    setSortColumnsState([])
  }, [])

  return {
    onFirstDataRendered,
    onSortChanged,
    onColumnMoved,
    onColumnResized,
    onColumnVisible,
    onFilterChanged,
    filteredColumns,
    sortColumnsState,
    onDeleteFilteredColumn,
    resetFilters,
    onDeleteSortedColumn,
    onDeleteSortedAllColumns,
    onChangeSortColumnState,
    columnState: base.columnState,
    saveColumnState: base.saveColumnState,
    saveFilterState: base.saveFilterState,
    resetDerivedState,
  }
}
